import { countries } from '../languages/translations/countries';

export type CountryOption = { value: string; label: string };

// Priority countries shown at the top of the list (Switzerland + neighbours + common nationalities)
const PRIORITY_CODES = ['CH', 'FR', 'DE', 'IT', 'AT', 'PT', 'ES', 'RS', 'TR'];

/**
 * Returns country options sorted by localized name, with Switzerland and common
 * countries pinned at the top separated from the rest of the alphabetical list.
 * @param langCode - one of 'fr', 'de', 'it', 'en', 'es' (case-insensitive)
 */
export function getCountryOptions(langCode: string): CountryOption[] {
  const key = langCode.toLowerCase() as 'fr' | 'de' | 'it' | 'en' | 'es';
  const getName = (c: typeof countries[number]) => c[key] || c.en;

  const priority: CountryOption[] = [];
  const rest: CountryOption[] = [];

  for (const c of countries) {
    const option = { value: c.code, label: getName(c) };
    if (PRIORITY_CODES.includes(c.code)) {
      priority.push(option);
    } else {
      rest.push(option);
    }
  }

  // Sort priority by their order in PRIORITY_CODES
  priority.sort((a, b) => PRIORITY_CODES.indexOf(a.value) - PRIORITY_CODES.indexOf(b.value));
  // Sort rest alphabetically by localized name
  rest.sort((a, b) => a.label.localeCompare(b.label, key));

  // Insert a visual separator between priority and rest
  if (priority.length > 0 && rest.length > 0) {
    priority.push({ value: '__separator__', label: '───────────' });
  }
  return priority.concat(rest);
}

/**
 * Returns the localized name of a country given its ISO alpha-2 code.
 */
export function getCountryName(code: string, langCode: string): string {
  const key = langCode.toLowerCase() as 'fr' | 'de' | 'it' | 'en' | 'es';
  const country = countries.find(c => c.code === code);
  return country ? (country[key] || country.en) : code;
}
